import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Progress,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  message,
} from "antd";
import { DollarOutlined, ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import apiClient from "../../api/apiClient";
import { useAuth } from "../../context/AuthContext";
import { useSelection } from "../../SelectionContext";
import { useCommercialTreasuryAccountSuggestion } from "../../hooks/useCommercialTreasurySuggestion";
import {
  loadReturnPurchasePaymentOptions,
  normalizeReturnPurchaseScopeId,
  ReturnPurchasePaymentOption,
} from "../returnPurchasePaymentOptions";

interface ReturnPurchasePaymentOrder {
  id: number;
  invoice_number?: string;
  total: number | string;
  paid_amount?: number | string;
  due_amount?: number | string;
  payment_status?: string;
  user_id: number;
  user_name?: string;
  entity_name?: string;
  company_id?: number;
  warehouse_id?: number;
}

interface ReturnPurchasePaymentModalProps {
  visible: boolean;
  onClose: () => void;
  order: ReturnPurchasePaymentOrder | null;
  onPaymentAdded?: () => void | Promise<void>;
}

const formatMoney = (value: unknown): string => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed)
    ? parsed.toLocaleString("fr-FR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })
    : "0";
};

const paymentStatusFromAmounts = (paid: number, total: number): string => {
  if (paid <= 0) return "Non payé";
  if (paid < total) return "Partiellement payé";
  return "Payé";
};

const generateIdempotencyKey = (): string =>
  `purchase-return-payment-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;

const ReturnPurchasePaymentModal: React.FC<
  ReturnPurchasePaymentModalProps
> = ({ visible, onClose, order, onPaymentAdded }) => {
  const [form] = Form.useForm();
  const [paymentModes, setPaymentModes] = useState<
    ReturnPurchasePaymentOption[]
  >([]);
  const [treasuryAccounts, setTreasuryAccounts] = useState<
    ReturnPurchasePaymentOption[]
  >([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const inFlightRef = useRef(false);
  const idempotencyKeyRef = useRef<string | null>(null);
  const { selectedCompany, selectedWarehouse } = useSelection();
  const { user, hasPermission } = useAuth();
  const canCreateIncomingPayment = hasPermission(
    "Gestion Commerciale.Ventes.PaiementsEntrants.create"
  );
  const { onModeChange: suggestTreasuryAccount } =
    useCommercialTreasuryAccountSuggestion(form, {
      warehouseId: order?.warehouse_id || selectedWarehouse,
      enabled: visible,
      modeField: "payment_mode_id",
      accountField: "treasury_account_id",
      accounts: treasuryAccounts,
    });

  const total = Number(order?.total ?? 0);
  const paid = Number(order?.paid_amount ?? 0);
  const remaining = Math.max(0, total - paid);
  const fullyPaid = remaining <= 0;
  const paymentPercentage =
    total > 0 ? Math.min(100, Math.max(0, Math.round((paid / total) * 100))) : 0;

  useEffect(() => {
    if (!visible || !order) return undefined;

    const companyId =
      normalizeReturnPurchaseScopeId(order.company_id) ??
      normalizeReturnPurchaseScopeId(selectedCompany);
    const warehouseId =
      normalizeReturnPurchaseScopeId(order.warehouse_id) ??
      normalizeReturnPurchaseScopeId(selectedWarehouse);
    let active = true;

    form.resetFields();
    form.setFieldsValue({
      amount: fullyPaid ? 0 : remaining,
      date: dayjs(),
      payment_mode_id: undefined,
      treasury_account_id: undefined,
      notes: "",
    });
    setPaymentModes([]);
    setTreasuryAccounts([]);
    setOptionsLoaded(false);
    setOptionsError(null);
    setSubmitError(null);
    inFlightRef.current = false;
    idempotencyKeyRef.current = null;

    if (!companyId || !warehouseId) {
      setOptionsError(
        "Entreprise ou établissement introuvable pour charger les options de paiement."
      );
      return undefined;
    }

    setLoadingOptions(true);
    loadReturnPurchasePaymentOptions(apiClient, companyId, warehouseId)
      .then(({ paymentModes: modes, treasuryAccounts: accounts }) => {
        if (!active) return;
        setPaymentModes(modes);
        setTreasuryAccounts(accounts);
      })
      .catch((error: any) => {
        if (!active) return;
        console.error(
          "Erreur lors du chargement des options de paiement du retour d'achat:",
          error
        );
        setOptionsError(
          error?.response?.data?.error ||
            error?.response?.data?.message ||
            error?.message ||
            "Impossible de charger les options de paiement."
        );
      })
      .finally(() => {
        if (active) {
          setLoadingOptions(false);
          setOptionsLoaded(true);
        }
      });

    return () => {
      active = false;
    };
  }, [
    form,
    fullyPaid,
    order,
    reloadToken,
    remaining,
    selectedCompany,
    selectedWarehouse,
    visible,
  ]);

  const closeModal = () => {
    setOptionsLoaded(false);
    onClose();
  };

  const handleSubmit = async (values: any) => {
    if (!order || inFlightRef.current) return;
    if (!canCreateIncomingPayment) {
      message.error(
        "Vous n'avez pas la permission d'enregistrer ce remboursement fournisseur."
      );
      return;
    }

    const companyId =
      normalizeReturnPurchaseScopeId(order.company_id) ??
      normalizeReturnPurchaseScopeId(selectedCompany);
    const warehouseId =
      normalizeReturnPurchaseScopeId(order.warehouse_id) ??
      normalizeReturnPurchaseScopeId(selectedWarehouse);
    const amount = Number(values.amount);

    if (!companyId || !warehouseId) {
      setSubmitError(
        "Entreprise ou établissement introuvable pour enregistrer le paiement."
      );
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0 || amount > remaining) {
      setSubmitError("Le montant saisi est invalide.");
      return;
    }
    if (
      !paymentModes.some((mode) => mode.id === Number(values.payment_mode_id)) ||
      !treasuryAccounts.some(
        (account) => account.id === Number(values.treasury_account_id)
      )
    ) {
      setSubmitError(
        "Le mode de paiement ou le compte de trésorerie sélectionné n'est plus disponible."
      );
      return;
    }

    inFlightRef.current = true;
    setSubmitting(true);
    setSubmitError(null);
    idempotencyKeyRef.current ??= generateIdempotencyKey();

    const newPaidAmount = paid + amount;
    const newDueAmount = Math.max(0, total - newPaidAmount);
    const payload = {
      payment: {
        company_id: companyId,
        warehouse_id: warehouseId,
        payment_type: "in",
        date: dayjs(values.date).format("YYYY-MM-DD"),
        amount,
        payment_mode_id: Number(values.payment_mode_id),
        treasury_account_id: Number(values.treasury_account_id),
        user_id: Number(order.user_id),
        notes:
          String(values.notes || "").trim() ||
          `Remboursement fournisseur - ${order.invoice_number || order.id}`,
        staff_user_id: user?.id || null,
      },
      order: {
        id: order.id,
        paid_amount: newPaidAmount,
        due_amount: newDueAmount,
        payment_status: paymentStatusFromAmounts(newPaidAmount, total),
      },
    };

    try {
      const response = await apiClient.post(
        "/api/payments/process-order-payment",
        payload,
        {
          headers: {
            "X-Idempotency-Key": idempotencyKeyRef.current,
          },
          timeout: 20000,
        }
      );

      if (response.data?.is_duplicate) {
        message.warning(
          `Le paiement ${response.data.payment_number || ""} avait déjà été enregistré.`
        );
      } else {
        message.success("Remboursement fournisseur enregistré avec succès.");
      }

      form.resetFields();
      closeModal();
      try {
        await onPaymentAdded?.();
      } catch (refreshError) {
        console.error(
          "Paiement enregistré, mais actualisation incomplète:",
          refreshError
        );
        message.warning(
          "Le paiement est enregistré, mais l'affichage n'a pas pu être actualisé."
        );
      }
    } catch (error: any) {
      console.error("Erreur lors du paiement du retour d'achat:", error);
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message;
      setSubmitError(
        errorMessage || "Impossible d'enregistrer le remboursement fournisseur."
      );
    } finally {
      inFlightRef.current = false;
      setSubmitting(false);
    }
  };

  const paymentOptionsUnavailable =
    optionsLoaded && !optionsError && paymentModes.length === 0;
  const treasuryOptionsUnavailable =
    optionsLoaded && !optionsError && treasuryAccounts.length === 0;

  return (
    <Modal
      title={
        <Space>
          <DollarOutlined style={{ color: "#1677ff" }} />
          <span>
            Ajouter un paiement - {order?.invoice_number || "Retour d'achat"}
          </span>
        </Space>
      }
      open={visible}
      onCancel={submitting ? undefined : closeModal}
      footer={null}
      width={640}
      destroyOnClose
      styles={{ body: { maxHeight: "calc(100vh - 150px)", overflowY: "auto" } }}
    >
      {order ? (
        <Spin spinning={submitting} tip="Enregistrement...">
          <Card size="small" style={{ marginBottom: 16 }}>
            <Descriptions
              bordered
              size="small"
              column={{ xs: 1, sm: 2 }}
            >
              <Descriptions.Item label="Fournisseur">
                {order.entity_name || order.user_name || "Non spécifié"}
              </Descriptions.Item>
              <Descriptions.Item label="Statut">
                <Tag color={fullyPaid ? "success" : "processing"}>
                  {paymentStatusFromAmounts(paid, total)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Montant du retour">
                {formatMoney(total)} XOF
              </Descriptions.Item>
              <Descriptions.Item label="Déjà encaissé">
                {formatMoney(paid)} XOF
              </Descriptions.Item>
              <Descriptions.Item label="Reste à encaisser" span={2}>
                <strong style={{ color: remaining > 0 ? "#cf1322" : "#389e0d" }}>
                  {formatMoney(remaining)} XOF
                </strong>
              </Descriptions.Item>
            </Descriptions>
            <Progress
              percent={paymentPercentage}
              status={fullyPaid ? "success" : "active"}
              style={{ marginTop: 12, marginBottom: 0 }}
            />
          </Card>

          {!canCreateIncomingPayment ? (
            <Alert
              type="warning"
              showIcon
              message="Permission insuffisante"
              description="Vous ne pouvez pas enregistrer de paiement entrant."
              style={{ marginBottom: 16 }}
            />
          ) : null}

          {optionsError ? (
            <Alert
              type="error"
              showIcon
              message="Options de paiement indisponibles"
              description={optionsError}
              action={
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={() => setReloadToken((value) => value + 1)}
                >
                  Réessayer
                </Button>
              }
              style={{ marginBottom: 16 }}
            />
          ) : null}

          {paymentOptionsUnavailable ? (
            <Alert
              type="warning"
              showIcon
              message="Aucun mode de paiement disponible"
              description="Configurez au moins un mode de paiement actif pour cette entreprise."
              style={{ marginBottom: 16 }}
            />
          ) : null}

          {treasuryOptionsUnavailable ? (
            <Alert
              type="warning"
              showIcon
              message="Aucun compte de trésorerie disponible"
              description="Configurez un compte commercial actif pour cet établissement."
              style={{ marginBottom: 16 }}
            />
          ) : null}

          {submitError ? (
            <Alert
              type="error"
              showIcon
              closable
              message="Paiement non enregistré"
              description={submitError}
              onClose={() => setSubmitError(null)}
              style={{ marginBottom: 16 }}
            />
          ) : null}

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            disabled={submitting || loadingOptions || fullyPaid}
          >
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="amount"
                  label="Montant reçu"
                  rules={[
                    { required: true, message: "Saisissez le montant reçu" },
                    {
                      validator: (_, value) => {
                        const numericValue = Number(value);
                        if (!Number.isFinite(numericValue) || numericValue <= 0) {
                          return Promise.reject(
                            new Error("Le montant doit être supérieur à zéro")
                          );
                        }
                        if (numericValue > remaining) {
                          return Promise.reject(
                            new Error(
                              `Le montant maximal est ${formatMoney(remaining)} XOF`
                            )
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <InputNumber
                    min={0.01}
                    max={remaining}
                    precision={2}
                    addonAfter="XOF"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="date"
                  label="Date de paiement"
                  rules={[{ required: true, message: "Sélectionnez une date" }]}
                >
                  <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="payment_mode_id"
                  label="Mode de paiement"
                  rules={[
                    { required: true, message: "Sélectionnez un mode de paiement" },
                  ]}
                >
                  <Select
                    onChange={suggestTreasuryAccount}
                    showSearch
                    optionFilterProp="label"
                    loading={loadingOptions}
                    placeholder="Sélectionner un mode"
                    options={paymentModes.map((mode) => ({
                      value: mode.id,
                      label: mode.name,
                    }))}
                    disabled={paymentModes.length === 0}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="treasury_account_id"
                  label="Compte de trésorerie"
                  rules={[
                    { required: true, message: "Sélectionnez un compte" },
                  ]}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    loading={loadingOptions}
                    placeholder="Sélectionner un compte"
                    options={treasuryAccounts.map((account) => ({
                      value: account.id,
                      label: account.currency_code
                        ? `${account.name} · ${account.currency_code}`
                        : account.name,
                    }))}
                    disabled={treasuryAccounts.length === 0}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="notes" label="Notes (optionnel)">
              <Input.TextArea
                rows={3}
                placeholder="Référence ou commentaire sur le remboursement"
              />
            </Form.Item>

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Space>
                <Button onClick={closeModal} disabled={submitting}>
                  Annuler
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={submitting}
                  disabled={
                    submitting ||
                    loadingOptions ||
                    fullyPaid ||
                    !canCreateIncomingPayment ||
                    paymentModes.length === 0 ||
                    treasuryAccounts.length === 0
                  }
                >
                  Enregistrer le paiement
                </Button>
              </Space>
            </div>
          </Form>
        </Spin>
      ) : null}
    </Modal>
  );
};

export default ReturnPurchasePaymentModal;
